/**
 * SC-1013 — la proposition de compte, posée dans l'étape « Coordonnées ».
 *
 * Un client non connecté retape ses coordonnées à chaque visite parce que rien,
 * dans le parcours, ne lui dit qu'il peut s'en dispenser. La question se pose
 * donc là où l'information vient d'être saisie : à cet endroit, créer le compte
 * ne lui coûte plus qu'un mot de passe — ou un clic avec Google.
 *
 * Elle ne coûte pas un écran à elle seule et ne bloque jamais l'envoi :
 * « Suivant » poursuit la commande sans compte.
 *
 * Une phrase, un bouton, un lien. Elle disait tout cela en quatre paragraphes
 * (intro, bouton, mention Google, mention « sans compte »), sous une autre
 * invitation qui répétait la même chose en haut de page : le client lisait six
 * fois « compte » avant d'avoir tapé son nom. Les mots qui comptent sont en gras
 * et les points en couleur, le reste s'efface.
 */
import { Trans, useTranslation } from 'react-i18next';
import { rewardFromLabel, type GuestNudge } from '@/features/loyalty/format';
import { NUDGE_EMPHASIS } from '@/features/loyalty/components/nudgeEmphasis';

export default function AccountInvite({
  onCreateAccount,
  onLogin,
  nudge,
}: {
  onCreateAccount: () => void;
  /** Le client a déjà un compte : c'est ici qu'il le découvre quand « Coordonnées »
   *  est la première étape (un seul mode), sans bandeau au-dessus. */
  onLogin: () => void;
  /** SC-1091 — ce que la commande rapporterait avec un compte (programme actif) :
   *  l'invitation le dit à la place de la phrase générique. Sans programme, absent. */
  nudge?: GuestNudge | null;
}) {
  const { t, i18n } = useTranslation();
  const intro =
    nudge?.kind === 'points' ? (
      <>
        <Trans i18nKey="checkout.account.introPoints" values={{ n: nudge.points }} components={NUDGE_EMPHASIS} />
        {/* SC-1640 — ce que valent ces points, comme le bandeau : quand « Coordonnées »
            est la première étape (un seul mode), c'est la seule phrase fidélité du paiement. */}
        {nudge.reward && ` ${rewardFromLabel(t, nudge.reward, i18n.language)}`}
      </>
    ) : nudge?.kind === 'counter' ? (
      <Trans i18nKey="checkout.account.introCounter" components={NUDGE_EMPHASIS} />
    ) : (
      <Trans i18nKey="checkout.account.intro" components={NUDGE_EMPHASIS} />
    );

  return (
    <div className="mt-3 rounded-btn-sm border-2 border-dashed border-ink/40 bg-cream px-3 py-2">
      <p className="m-0 font-mono text-sm leading-relaxed text-ink" data-testid="checkout-account-intro">
        {nudge && <span aria-hidden="true">🎁 </span>}
        {intro}
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          type="button"
          onClick={onCreateAccount}
          data-testid="checkout-create-account"
          className="min-h-[44px] rounded-btn-sm border-2 border-solid border-ink bg-mint px-4 font-display text-sm text-ink"
        >
          {t('checkout.account.createCta')}
        </button>
        <span className="font-mono text-sm text-ink/70">
          {t('checkout.account.haveAccount')}{' '}
          <button
            type="button"
            onClick={onLogin}
            data-testid="checkout-account-login"
            className="min-h-[44px] border-0 bg-transparent p-0 font-mono text-sm font-bold text-ink underline"
          >
            {t('loyalty.guest.login')}
          </button>
        </span>
      </div>
    </div>
  );
}
